// src/AssetManager/ArchivedAufFieldsTable.jsx
import * as React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Button,
  Chip,
  Tooltip,
  Link,
} from '@mui/material';

/**
 * ArchivedAufFieldsTable
 * ---------------------------------------------------------------------------
 * Read-only view of fields that were archived from the Asset Upload Form.
 *
 * Expects entries like:
 *   {
 *     ufi: string | null,
 *     label: string | null,
 *     type: string | null,
 *     formId: string | null,
 *     assetFieldRole: string | null,
 *     semanticKey: string | null,
 *     optionsSetUFI: string | null,
 *     archivedAt: string (ISO) | null,
 *     wasLibraryFilter: boolean | undefined   // optional, future-aware
 *   }
 *
 * In v1, "Library Filter" is derived from:
 *   - wasLibraryFilter (if present), otherwise
 *   - (future) explicit mapping metadata
 *
 * `onRowAction(action, row)` is reserved for future mapping/restore flows.
 */

const prettyType = (raw) => {
  const t = String(raw || '').trim();
  if (!t) return '—';
  if (t === 'single-select') return 'Single select';
  if (t === 'multi-select') return 'Multi select';
  if (t === 'date-picker') return 'Date';
  if (t === 'multi-line-text') return 'Multi-line text';
  if (t === 'urlField') return 'URL';
  return t.charAt(0).toUpperCase() + t.slice(1);
};

const formatArchivedAt = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
};

export default function ArchivedAufFieldsTable({
  archivedFields = [],
  onRowAction,
}) {
  const rows = React.useMemo(
    () => (Array.isArray(archivedFields) ? archivedFields : []),
    [archivedFields],
  );

  if (!rows.length) {
    // No archived fields → don't render a table at all
    return null;
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Archived Asset Fields
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 2, maxWidth: 640 }}
      >
        These fields were removed from the Asset Upload Form. You can review
        them here and plan mapping or restore flows later.
      </Typography>

      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          maxHeight: 360,
          width: '100%',
          overflowX: 'auto', // ✅ allow horizontal scroll when needed
          // Make table text more compact
          '& .MuiTableCell-root': {
            fontSize: 12,
            py: 0.5,
            whiteSpace: 'nowrap', // ✅ keep cells compact; avoids wrapping that forces height growth
          },
          '& .MuiTableHead-root .MuiTableCell-root': {
            fontWeight: 600,
          },
        }}
      >
        {/* ✅ minWidth ensures the table can be wider than the container and scroll horizontally */}
        <Table size="small" stickyHeader sx={{ minWidth: 760 }}>
          <TableHead>
            <TableRow>
              <TableCell>Field label</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Library Filter</TableCell>
              <TableCell>UFI prefix</TableCell>
              <TableCell>Archived at</TableCell>
              <TableCell align="right">Inspect</TableCell>
              <TableCell align="right">Restore</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, idx) => {
              const ufi = row.ufi || null;
              const ufiPrefix = ufi ? String(ufi).slice(0, 8) : '—';

              const wasFilter = row.wasLibraryFilter === true;

              const canAct = typeof onRowAction === 'function';

              //  Option A / 2B UX: show restore state and prevent double-restore clicks
              const isRestored = !!row.restoredAt;
              const pendingPublish = row.restoredPendingPublish === true;

              // "pending publish" should NOT disable restore,
              // because it may not be persisted across sessions.
              const restoreDisabled = !canAct || !row?.ufi || !row?.type;
              // Create a stable unique key per archive entry.
              // archivedAt is the natural unique timestamp for a specific archive event.
              // Fallbacks ensure we never collide even if archivedAt is missing.
              const rowKey = `${ufi || 'no-ufi'}_${row.archivedAt || idx}`;

              return (
                <TableRow key={rowKey}>
                  <TableCell>
                    {row.label || '—'}

                    {/* ✅ Restore state chip */}
                    {pendingPublish && (
                      <Chip
                        size="small"
                        label="Pending publish"
                        sx={{ ml: 0.75, fontSize: 10, height: 20 }}
                      />
                    )}
                    {isRestored && !pendingPublish && (
                      <Chip
                        size="small"
                        label="Restored"
                        sx={{ ml: 0.75, fontSize: 10, height: 20 }}
                      />
                    )}

                    {row.assetFieldRole && (
                      <Chip
                        size="small"
                        label={row.assetFieldRole}
                        sx={{ ml: 0.75, fontSize: 10, height: 20 }}
                      />
                    )}
                  </TableCell>


                  <TableCell>{prettyType(row.type)}</TableCell>

                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: 12, color: 'text.secondary' }}
                    >
                      {wasFilter ? 'Yes' : 'No'}
                    </Typography>
                  </TableCell>

                  <TableCell>{ufiPrefix}</TableCell>

                  <TableCell>{formatArchivedAt(row.archivedAt)}</TableCell>

                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="text"
                      disabled={!canAct}
                      onClick={() => onRowAction?.('inspect', row)}
                      sx={{ fontSize: 12 }}
                    >
                      Inspect
                    </Button>
                  </TableCell>

                  {/* New Restore column: linked text + tooltip (not a button) */}
                  <TableCell align="right">
                    <Tooltip
                      title={
                        restoreDisabled
                          ? 'Restore is unavailable'
                          : pendingPublish
                            ? 'This field was restored previously but not published. Restoring again will reopen the builder and re-add it if it’s missing.'
                            : isRestored
                              ? 'This field was restored previously. Restoring again will reopen the builder and re-add it if it’s missing.'
                              : 'Restore this field back into the Asset Upload Form (preserves UFI & optionIds). Publish the form to make it available everywhere.'
                      }
                      arrow
                    >
                      <span>
                        <Link
                          component="button"
                          type="button"
                          underline="always"
                          onClick={() => {
                            if (restoreDisabled) return;
                            onRowAction?.('restore', row);
                          }}
                          sx={{
                            fontSize: 12,
                            cursor: restoreDisabled ? 'not-allowed' : 'pointer',
                            color: restoreDisabled ? 'text.disabled' : 'primary.main',
                            background: 'none',
                            border: 'none',
                            p: 0,
                            m: 0,
                            textTransform: 'none',
                            pointerEvents: restoreDisabled ? 'none' : 'auto',
                          }}
                        >
                          {pendingPublish ? 'Restore again' : 'Restore'}
                        </Link>
                      </span>
                    </Tooltip>

                  </TableCell>

                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

ArchivedAufFieldsTable.propTypes = {
  archivedFields: PropTypes.arrayOf(
    PropTypes.shape({
      ufi: PropTypes.string,
      label: PropTypes.string,
      type: PropTypes.string,
      formId: PropTypes.string,
      assetFieldRole: PropTypes.string,
      semanticKey: PropTypes.string,
      optionsSetUFI: PropTypes.string,
      archivedAt: PropTypes.string,
      wasLibraryFilter: PropTypes.bool,
      restoredAt: PropTypes.string,
      restoredBy: PropTypes.string,
      restoredFieldId: PropTypes.string,
      restoredPendingPublish: PropTypes.bool,
    }),
  ),
  onRowAction: PropTypes.func,
};
